import { cn } from '@/lib/cn';
import type { FileUpload } from '@innermate/shared';
import {
  AlertCircle,
  Ban,
  Check,
  File,
  FileSpreadsheet,
  FileText,
  Image as ImageIcon,
  Loader2,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';

/**
 * 칩 표시 단계. 같은 칩이라도 어디서 쓰이느냐에 따라 진행 상황이 다르므로 phase 로 통일.
 *
 * - `pending`: 사용자가 드롭/선택만 한 상태. 아직 업로드 시작 전. 메시지를 전송해야 처리 시작
 * - `uploading`: 메시지 전송 직후 MinIO 업로드 진행 중
 * - `uploaded`: 업로드 완료, 서버의 excel.ingest 워커 진입 대기
 * - `processing`: 워커가 실제 시트 파싱 중
 * - `ready`: 분석 준비 완료
 * - `failed`: 처리 실패
 */
export type AttachmentChipPhase =
  | 'pending'
  | 'uploading'
  | 'uploaded'
  | 'queued'
  | 'processing'
  | 'ready'
  | 'failed'
  | 'canceled';

export interface ChipFile {
  /** 칩 식별자. 업로드된 파일은 FileUpload.id, pending 파일은 클라이언트 localId. */
  id: string;
  originalName: string;
  /** pending 단계에서는 File.size, 업로드 후엔 서버 측 sizeBytes. 0 도 허용. */
  sizeBytes: number;
  kind: FileUpload['kind'];
  /** 칩에 표시할 단계. 호출자가 서버 status / 클라 진행 상태를 종합해 결정. */
  phase: AttachmentChipPhase;
  /** failed 일 때 hover 에 보일 에러 메시지. */
  error?: string;
  /** true 면 회색 폴백 chip (메시지 시점엔 존재했으나 현재 삭제됨). */
  missing?: boolean;
  /** 처리 시작 시각(epoch ms, 보통 서버 createdAt). 진행 중 phase 에 경과 타이머 표시용. */
  startedAt?: number;
  /** queued 일 때 앞에 대기 중인 같은 사용자 작업 수. "대기 중 · 앞에 N건" 표시용. */
  queueAhead?: number | null;
}

/**
 * 진행 중(경과 타이머/폴링 대상) phase 인지 — pending 은 아직 시작 전이라 제외.
 * 'uploaded' 는 업로드가 끝난 휴지 상태(엑셀 lazy)라 타이머를 돌리지 않는다 — '완료'로 표시.
 */
function isActivePhase(phase: AttachmentChipPhase): boolean {
  return phase === 'uploading' || phase === 'queued' || phase === 'processing';
}

/** ms → "m:ss". */
function formatElapsedClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

interface Props {
  files: ChipFile[];
  onRemove?: (id: string) => void;
  size?: 'sm' | 'md';
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

type BadgeIcon = 'spinner' | 'check' | 'alert' | 'ban';
interface BadgeSpec {
  text: string;
  tone: 'muted' | 'danger' | 'success';
  icon: BadgeIcon;
  title: string;
}

function badgeFor(phase: AttachmentChipPhase, error?: string): BadgeSpec | null {
  switch (phase) {
    case 'pending':
      return null;
    case 'uploading':
      return {
        text: '업로드 중',
        tone: 'muted',
        icon: 'spinner',
        title: '파일을 서버로 업로드하는 중입니다.',
      };
    case 'uploaded':
      // 업로드가 끝난 휴지 상태 (엑셀은 메시지 전송 시 lazy 분석). 사용자에게는 "준비됨"으로 보여
      // 증가하는 타이머/스피너로 불필요한 대기를 유발하지 않는다.
      return {
        text: '완료',
        tone: 'success',
        icon: 'check',
        title: '업로드 완료. 메시지를 보내면 분석을 시작합니다.',
      };
    case 'queued':
      return {
        text: '대기 중',
        tone: 'muted',
        icon: 'spinner',
        title: '동시 변환 한도로 대기 중입니다. 순서가 되면 자동으로 시작합니다.',
      };
    case 'processing':
      return {
        text: '처리 중',
        tone: 'muted',
        icon: 'spinner',
        title: '파일 내용을 추출·분석하는 중입니다.',
      };
    case 'ready':
      return {
        text: '완료',
        tone: 'success',
        icon: 'check',
        title: '분석 준비 완료. 질문을 입력하세요.',
      };
    case 'failed':
      return {
        text: '실패',
        tone: 'danger',
        icon: 'alert',
        title: error ?? '파일 처리에 실패했습니다.',
      };
    case 'canceled':
      return {
        text: '중지됨',
        tone: 'danger',
        icon: 'ban',
        title: error ?? '처리가 중지되었습니다.',
      };
  }
}

/** 진행 중(중지 가능) phase 인지. */
export function isCancelablePhase(phase: AttachmentChipPhase): boolean {
  return (
    phase === 'uploading' || phase === 'uploaded' || phase === 'queued' || phase === 'processing'
  );
}

/**
 * 아직 종료(ready/failed/canceled)되지 않은 서버 status 인지. true 면 워커가 status 를
 * 갱신할 여지가 있어 프런트가 폴링을 계속해야 한다. terminal 이면 폴링 중단.
 */
export function isPendingStatus(status: FileUpload['status']): boolean {
  return isCancelablePhase(statusToPhase(status));
}

/** 처리가 20초 넘게 이어지면 "오래 걸릴 수 있음" 힌트로 tooltip 전환. */
const SLOW_HINT_MS = 20_000;

export function AttachmentChips({ files, onRemove, size = 'md' }: Props) {
  // 진행 중(경과 타이머 표시) 칩이 하나라도 있으면 1초마다 re-render. 없으면 멈춰 무한 타이머 방지.
  const hasLiveTimer = files.some(
    (f) => !f.missing && isActivePhase(f.phase) && f.startedAt != null,
  );
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    if (!hasLiveTimer) return;
    const t = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [hasLiveTimer]);

  if (files.length === 0) return null;
  const isSm = size === 'sm';
  return (
    <div className={cn('flex flex-wrap gap-1.5', isSm ? 'mt-1' : 'mb-2')}>
      {files.map((f) => {
        const Icon =
          f.kind === 'excel'
            ? FileSpreadsheet
            : f.kind === 'document'
              ? FileText
              : f.kind === 'image'
                ? ImageIcon
                : File;
        const badge = f.missing ? null : badgeFor(f.phase, f.error);
        if (badge) {
          const elapsedMs = f.startedAt != null ? nowTick - f.startedAt : 0;
          // 대기 중: "앞에 N건" 표시. 진행 중: 경과 타이머. 처리 지연: 대용량 힌트.
          if (f.phase === 'queued' && f.queueAhead != null && f.queueAhead > 0) {
            badge.text = `대기 중 · 앞에 ${f.queueAhead}건`;
          }
          if (isActivePhase(f.phase) && f.startedAt != null) {
            badge.text = `${badge.text} · ${formatElapsedClock(elapsedMs)}`;
          }
          if (f.phase === 'processing' && elapsedMs > SLOW_HINT_MS) {
            badge.title = '문서가 커서 변환에 시간이 걸릴 수 있어요. 완료되면 자동으로 표시됩니다.';
          }
        }
        const pendingTitle =
          !f.missing && f.phase === 'pending' ? '메시지를 전송하면 분석을 시작합니다.' : undefined;
        return (
          <span
            key={f.id}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md border border-border bg-surface-muted/60',
              isSm ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-sm',
              f.missing && 'opacity-60',
            )}
            title={pendingTitle}
          >
            <Icon className={cn(isSm ? 'size-3' : 'size-3.5', 'text-foreground-muted shrink-0')} />
            <span className="truncate max-w-[200px]" title={f.originalName}>
              {f.missing ? `${f.originalName} (삭제됨)` : f.originalName}
            </span>
            {!f.missing ? (
              <span className="text-foreground-muted shrink-0">{formatSize(f.sizeBytes)}</span>
            ) : null}
            {badge ? (
              <span
                className={cn(
                  'inline-flex items-center gap-1 shrink-0',
                  badge.tone === 'danger'
                    ? 'text-destructive'
                    : badge.tone === 'success'
                      ? 'text-emerald-600'
                      : 'text-foreground-muted',
                )}
                title={badge.title}
              >
                {badge.icon === 'spinner' ? (
                  <Loader2 className={cn(isSm ? 'size-3' : 'size-3.5', 'animate-spin')} />
                ) : badge.icon === 'check' ? (
                  <Check className={isSm ? 'size-3' : 'size-3.5'} />
                ) : badge.icon === 'ban' ? (
                  <Ban className={isSm ? 'size-3' : 'size-3.5'} />
                ) : (
                  <AlertCircle className={isSm ? 'size-3' : 'size-3.5'} />
                )}
                {badge.text}
              </span>
            ) : null}
            {onRemove ? (
              <button
                type="button"
                onClick={() => onRemove(f.id)}
                aria-label={`${f.originalName} 제거`}
                className="ml-0.5 grid place-items-center rounded-sm text-foreground-muted hover:text-foreground hover:bg-surface-muted"
              >
                <X className={isSm ? 'size-3' : 'size-3.5'} />
              </button>
            ) : null}
          </span>
        );
      })}
    </div>
  );
}

/** 서버 측 FileUpload.status 를 phase 로 매핑. 호출자가 클라이언트 단계를 더할 수 있다. */
export function statusToPhase(status: FileUpload['status']): AttachmentChipPhase {
  switch (status) {
    case 'uploading':
      return 'uploading';
    case 'uploaded':
      return 'uploaded';
    case 'queued':
      return 'queued';
    case 'processing':
      return 'processing';
    case 'ready':
      return 'ready';
    case 'failed':
      return 'failed';
    case 'canceled':
      return 'canceled';
  }
}
